import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import {
  AdminRegistryService,
  RegionStatus,
  RegistryRegion,
} from '../../../core/services/admin-registry.service';
import { LoggerService } from '../../../core/services/logger.service';
import { ToastService } from '../../../core/services/toast.service';
import { TranslationService } from '../../../core/services/translation.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

interface AdminRegionRow extends RegistryRegion {
  building: boolean;
  showLog: boolean;
  /** When this row was first observed as 'building', client-side only (not persisted) --
   *  drives the live elapsed-time counter so "still working" is visibly distinct from
   *  "stuck", since the server-side log text otherwise sits unchanged for the whole
   *  ingestion step regardless of how long it takes. */
  buildStartedAt: number | null;
}

/** How often to re-poll while any region is mid-build, to reflect status/log changes. */
const POLL_INTERVAL_MS = 4000;

/** How often the elapsed-time counter ticks -- independent of POLL_INTERVAL_MS, so the
 *  counter visibly moves between polls too, proving the page itself is alive. */
const TICK_INTERVAL_MS = 1000;

/**
 * Super-admin-only page to self-serve trigger an address-registry region build (Overture
 * ingestion + tippecanoe/pmtiles generation), instead of an engineer running the CLI
 * scripts by hand (see backend/src/registry/pipeline.ts). Regions themselves come from a
 * curated, engineer-maintained catalog (backend/src/registry/region-catalog.ts) — this page
 * only starts/tracks builds, it never lets an admin draw or edit a region's boundary.
 */
@Component({
  selector: 'app-admin-regions',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    ButtonComponent,
    CardComponent,
    CardContentComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
  templateUrl: './admin-regions.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-regions.component.css',
})
export class AdminRegionsComponent implements OnInit, OnDestroy {
  private adminRegistry = inject(AdminRegistryService);
  private toast = inject(ToastService);
  private translate = inject(TranslationService);
  private log = inject(LoggerService).getLogger('admin-regions');

  state = signal<'loading' | 'loaded' | 'error'>('loading');
  rows = signal<AdminRegionRow[]>([]);

  searchTerm = signal('');
  searchForm = new FormGroup({});
  searchModel = { term: '' };
  searchFields: FormlyFieldConfig[] = [];

  filteredRows = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const all = this.rows();
    if (!term) return all;
    return all.filter((r) => r.name.toLowerCase().includes(term) || r.slug.toLowerCase().includes(term));
  });

  /** Ticks every second while mounted, purely to force elapsedLabel() to recompute --
   *  read it (don't just rely on its side effect) wherever "now" matters, so change
   *  detection has a reason to notice. Local elapsed-time display only (buildStartedAt
   *  below is also a local stamp), so the local clock is fine -- ClockService's 30s
   *  granularity would visibly degrade this page's per-second ticker for no benefit. */
  // eslint-disable-next-line no-restricted-syntax
  now = signal(Date.now());

  private pollHandle: ReturnType<typeof setTimeout> | null = null;
  private tickHandle: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.searchFields = [
      {
        key: 'term',
        type: 'text',
        props: {
          placeholder: this.translate.translate('adminRegions.searchPlaceholder'),
          attributes: { 'data-testid': 'admin-region-search-input', autocomplete: 'off' },
        },
      },
    ];
    this.searchForm.valueChanges.subscribe((v: { term?: string }) => this.searchTerm.set(v.term ?? ''));

    // eslint-disable-next-line no-restricted-syntax -- local ticker, see `now` field above
    this.tickHandle = setInterval(() => this.now.set(Date.now()), TICK_INTERVAL_MS);
    this.load();
  }

  ngOnDestroy(): void {
    if (this.pollHandle) clearTimeout(this.pollHandle);
    if (this.tickHandle) clearInterval(this.tickHandle);
  }

  private load(): void {
    // Distinguishes the initial page load from a background poll refresh -- a poll fires
    // repeatedly while any region is building (see scheduleNextPollIfNeeded), and a
    // transient failure there (a dropped connection, a slow server mid-import) used to blow
    // away the whole already-loaded table in favor of a full-page error screen, AND silently
    // stop polling forever afterward (the old code only called scheduleNextPollIfNeeded from
    // the success branch) -- so a build already in progress became invisible with no way to
    // recover short of a manual page refresh. A poll failure now just toasts and keeps
    // retrying, leaving the last known rows on screen; only the initial load (nothing to
    // show yet) still uses the full-page error state.
    const isPoll = this.state() === 'loaded';

    this.adminRegistry.getRegions().subscribe({
      next: (response) => {
        const previous = new Map(this.rows().map((r) => [r.slug, r]));
        this.rows.set(
          response.regions.map((region) => {
            const prior = previous.get(region.slug);
            return {
              ...region,
              building: prior?.building ?? false,
              showLog: prior?.showLog ?? false,
              // Keep the existing stamp while still building; if we're seeing 'building'
              // for the first time (either just triggered via build() below, or a fresh
              // page load catching a build already underway elsewhere), start the clock
              // now -- an approximation for the latter case, but still an honest lower
              // bound ("at least this long"), and far better than no signal at all. Local
              // stamp compared only against this same device's `now` ticker above.
              // eslint-disable-next-line no-restricted-syntax
              buildStartedAt: region.status === 'building' ? (prior?.buildStartedAt ?? Date.now()) : null,
            };
          }),
        );
        this.state.set('loaded');
        this.scheduleNextPollIfNeeded();
      },
      error: (err) => {
        this.log.error('Failed to load regions:', err);
        if (isPoll) {
          this.toast.error(this.translate.translate('adminRegions.pollError'));
          this.scheduleNextPollIfNeeded(); // rows are unchanged, so this correctly resumes if still building
        } else {
          this.state.set('error');
        }
      },
    });
  }

  private scheduleNextPollIfNeeded(): void {
    if (this.pollHandle) clearTimeout(this.pollHandle);
    const anyBuilding = this.rows().some((r) => r.status === 'building');
    if (!anyBuilding) return;
    this.pollHandle = setTimeout(() => this.load(), POLL_INTERVAL_MS);
  }

  canBuild(row: AdminRegionRow): boolean {
    return !row.building && row.status !== 'building' && row.status !== 'ready';
  }

  buildLabel(row: AdminRegionRow): string {
    return this.translate.translate(row.status === 'failed' ? 'adminRegions.rebuild' : 'adminRegions.build');
  }

  statusLabel(status: RegionStatus): string {
    return this.translate.translate(`adminRegions.status.${status}`);
  }

  formattedDate(timestamp: number | null): string {
    return timestamp ? new Date(timestamp).toLocaleString() : this.translate.translate('adminRegions.never');
  }

  /** Live "Xs" / "Xm Ys" elapsed since a building row started -- reads now() so it
   *  recomputes on every tick, not just on load()'s 4s poll cadence. */
  elapsedLabel(row: AdminRegionRow): string {
    if (!row.buildStartedAt) return '';
    const totalSeconds = Math.max(0, Math.floor((this.now() - row.buildStartedAt) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  }

  toggleLog(row: AdminRegionRow): void {
    this.rows.update((rows) =>
      rows.map((r) => (r.slug === row.slug ? { ...r, showLog: !r.showLog } : r)),
    );
  }

  build(row: AdminRegionRow): void {
    this.rows.update((rows) =>
      // eslint-disable-next-line no-restricted-syntax -- local stamp, see `now` field above
      rows.map((r) => (r.slug === row.slug ? { ...r, building: true, buildStartedAt: Date.now() } : r)),
    );

    this.adminRegistry.buildRegion(row.slug).subscribe({
      next: () => {
        this.toast.success(this.translate.translate('adminRegions.buildStarted', { name: row.name }));
        this.load(); // picks up the new 'building' status and starts polling
      },
      error: (err) => {
        this.log.error(`Failed to start build for ${row.slug}:`, err);
        this.toast.error(this.translate.translate('adminRegions.buildFailedToStart', { name: row.name }));
        this.rows.update((rows) =>
          rows.map((r) => (r.slug === row.slug ? { ...r, building: false, buildStartedAt: null } : r)),
        );
      },
    });
  }
}
